import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { HubModuleType, ICreateCustomHtmlHubModuleDTO, ICustomHtmlHubModuleDTO, IEventHubsDAO, } from '../../../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../../../api/implementations/event-hubs.dao';
import { IErrorParsing } from '../../../../../../../../infrastructure/error-parsing';
import { IRouter } from '../../../../../../../../infrastructure/router';
import { RouteParams } from '../../../../../../../../routes-table';
import { RADIX_PARAM } from '../../../../../../../../constants/common.constants';
import { errorText } from '../../../../../../../../constants/common-labels.constants';
import { MODULE_CUSTOM_HTML_ZIP_UPLOAD_FAILED_ERROR_TEXT } from '../../../../../../event-hubs.text';
import { alertServerError } from '../../../../../../../global/modal/alerts-host/alert-templates';
import { validateCustomHtmlZipContent, ZipContentError } from './custom-html-zip.validator';
import { CUSTOM_HTML_MODULE_CONFIG, IFRAME_HEIGHT, LABEL, OBJECT_KEY, POSITION_ORDER, ZIP_FILE, } from './custom-html-module.config';
import { HubModuleVM } from '../hub-module.viewmodel';

export class CustomHtmlModuleVM extends HubModuleVM {
    public static createEmpty(positionOrder: number): CustomHtmlModuleVM {
        return new CustomHtmlModuleVM({ positionOrder: positionOrder });
    }

    public static createFromDTO(dto: ICustomHtmlHubModuleDTO | ICreateCustomHtmlHubModuleDTO): CustomHtmlModuleVM {
        const hasFile = !!dto.html?.objectKey;
        return new CustomHtmlModuleVM({
            ...dto,
            [OBJECT_KEY]: dto.html?.objectKey ?? '',
            [ZIP_FILE]: hasFile ? { name: dto.html.title } as File : null,
        });
    }

    private constructor(
        module: Partial<ICustomHtmlHubModuleDTO> & Record<string, unknown>,
        @Inject private readonly routingService: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
    ) {
        super(module.moduleId, 'CustomHtmlModuleVM', CUSTOM_HTML_MODULE_CONFIG, module);
        this.isFileValidated = !!module[OBJECT_KEY];
        this.savedSnapshot = this.serialize();
    }

    @observable
    public isUploading: boolean = false;

    @observable
    public fileError: ZipContentError | null = null;

    @observable
    private isFileValidated: boolean = false;

    public getModuleType = (): HubModuleType => {
        return HubModuleType.CUSTOM_HTML;
    };

    public clone = (): CustomHtmlModuleVM => {
        const dto = this.isNew() ? this.toCreateDTO() : this.toUpdateDTO() as ICustomHtmlHubModuleDTO;
        const vm = CustomHtmlModuleVM.createFromDTO(dto);
        this.copyIdentityTo(vm);
        return vm;
    };

    public isValid = (): boolean => {
        return this.isVmValid() && this.isFileValidated && !this.isUploading && !this.fileError;
    };

    public toCreateDTO = (): ICreateCustomHtmlHubModuleDTO => {
        return {
            positionOrder: this.getNumberValue(POSITION_ORDER),
            moduleType: HubModuleType.CUSTOM_HTML,
            enabled: this.isEnabled(),
            label: this.getTrimmedStringValue(LABEL),
            // the label is never displayed for this module
            displayLabel: false,
            html: {
                objectKey: this.getStringValue(OBJECT_KEY),
                title: this.getFileName(),
            },
            iframeHeight: this.getNumberValue(IFRAME_HEIGHT),
        };
    };

    public getFileName = (): string => {
        const zipFile = this.getFormVmValuePropsByKey(ZIP_FILE).value as File | null;
        return zipFile?.name ?? '';
    };

    @action.bound
    public async selectFile(file: File): Promise<void> {
        if (this.isUploading) {
            return;
        }

        this.setValue(ZIP_FILE, file);
        this.setValueTouched(ZIP_FILE);
        this.setValue(OBJECT_KEY, '');
        this.isFileValidated = false;
        this.fileError = null;

        if (!this.isValueValid(ZIP_FILE)) {
            return;
        }

        this.isUploading = true;
        try {
            const contentError = await validateCustomHtmlZipContent(file);
            if (contentError) {
                runInAction(() => {
                    this.fileError = contentError;
                });
                return;
            }

            const { objectKey } = await this.eventHubsDAO.uploadCustomHtmlZip(this.getAccountId(), this.getEventHubId(), file);
            runInAction(() => {
                this.setValue(OBJECT_KEY, objectKey);
                this.isFileValidated = true;
            });
        } catch (error) {
            runInAction(() => {
                alertServerError(errorText(), this.errorParsingService.extractMessage(error));
                this.fileError = { descriptor: MODULE_CUSTOM_HTML_ZIP_UPLOAD_FAILED_ERROR_TEXT };
            });
        } finally {
            runInAction(() => {
                this.isUploading = false;
            });
        }
    }

    private getAccountId = (): number => {
        return parseInt(this.routingService.router.getState().params.accountId, RADIX_PARAM);
    };

    private getEventHubId = (): number => {
        return parseInt(this.routingService.router.getState().params.eventHubId, RADIX_PARAM);
    };
}
